import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2, Loader2 } from "lucide-react";
import { fetchAdSettings, adminSaveAdSettings, formatApiError } from "@/lib/api";
import AdminAdModeSwitch from "@/components/AdminAdModeSwitch";

const inputCls = "w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500";

export default function AdminAdSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { fetchAdSettings().then(setS).catch(() => setS({ price: 99, duration_days: 7, pricing_model: "per_pin", max_pins: 20 })); }, []);
  if (!s) return null;
  const num = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value === "" ? "" : Number(e.target.value) }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setS(await adminSaveAdSettings(s)); toast.success("Advertising pricing saved — applies to new campaigns"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const example = s.pricing_model === "per_pin" ? `₹${s.price} × 3 PIN = ₹${(Number(s.price) || 0) * 3}` : `₹${s.price} for any number of PINs`;
  return (
    <>
    <AdminAdModeSwitch />
    <form onSubmit={save} className="border border-slate-100 rounded-2xl p-4 mb-5 bg-slate-50/60" data-testid="admin-ad-settings">
      <div className="flex items-center gap-2 font-semibold text-slate-900 mb-3"><Settings2 className="w-4 h-4 text-emerald-700" /> Advertising pricing & limits</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <label className="text-xs font-semibold text-slate-500">Base price (₹)<input type="number" min={0} required value={s.price} onChange={num("price")} className={inputCls} data-testid="ad-settings-price" /></label>
        <label className="text-xs font-semibold text-slate-500">Duration (days)<input type="number" min={1} max={365} required value={s.duration_days} onChange={num("duration_days")} className={inputCls} data-testid="ad-settings-duration" /></label>
        <label className="text-xs font-semibold text-slate-500">Pricing model
          <select value={s.pricing_model} onChange={(e) => setS((x) => ({ ...x, pricing_model: e.target.value }))} className={inputCls} data-testid="ad-settings-model">
            <option value="per_pin">Price per PIN code</option>
            <option value="fixed">Fixed price per campaign</option>
          </select>
        </label>
        <label className="text-xs font-semibold text-slate-500">Max PINs per ad<input type="number" min={1} max={10000} required value={s.max_pins} onChange={num("max_pins")} className={inputCls} data-testid="ad-settings-max-pins" /></label>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mt-3">
        <div className="text-xs text-slate-500" data-testid="ad-settings-example">Example: {example} · {s.duration_days} days. Existing campaigns keep their price.</div>
        <button disabled={busy} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs font-semibold disabled:opacity-60" data-testid="ad-settings-save">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save settings"}</button>
      </div>
    </form>
    </>
  );
}
